import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Eye, EyeOff, Search } from 'lucide-react';
import { adminGetServices, adminCreateService, adminUpdateService, adminDeleteService } from '../../lib/api';
import { slugify, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { ImageUploader } from '../../components/ImageUploader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const emptyService = {
  title_de: '',
  title_en: '',
  slug: '',
  short_de: '',
  short_en: '',
  long_de: '',
  long_en: '',
  icon: 'bi-tools',
  image_url: '',
  gallery: [],
  features: [],
  features_en: [],
  target_audience: '',
  target_audience_en: '',
  process: '',
  process_en: '',
  benefits: [],
  benefits_en: [],
  is_active: true,
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
};

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState(emptyService);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchServices = async () => {
    try {
      const res = await adminGetServices();
      setServices(res.data);
      setFilteredServices(res.data);
    } catch (err) {
      console.error('Error fetching services:', err);
      toast.error('Gabim gjatë marrjes së shërbimeve');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredServices(services);
      return;
    }
    const filtered = services.filter(s => 
      s.title_de.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredServices(filtered);
  }, [searchTerm, services]);

  const handleOpenCreate = () => {
    setSelectedService(null);
    setFormData(emptyService);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (service) => {
    setSelectedService(service);
    setFormData({
      ...service,
      title_en: service.title_en || '',
      short_en: service.short_en || '',
      long_en: service.long_en || '',
      features: service.features || [],
      features_en: service.features_en || [],
      benefits: service.benefits || [],
      benefits_en: service.benefits_en || [],
      gallery: service.gallery || [],
      target_audience: service.target_audience || '',
      target_audience_en: service.target_audience_en || '',
      process: service.process || '',
      process_en: service.process_en || '',
      meta_title: service.meta_title || '',
      meta_description: service.meta_description || '',
      meta_keywords: service.meta_keywords || '',
    });
    setIsDialogOpen(true);
  };

  const handleOpenDelete = (service) => {
    setSelectedService(service);
    setIsDeleteDialogOpen(true);
  };

  const handleTitleChange = (value) => {
    setFormData({
      ...formData,
      title_de: value,
      slug: slugify(value),
    });
  };

  const handleArrayChange = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData({ ...formData, [field]: items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedService) {
        await adminUpdateService(selectedService.id, formData);
        toast.success('Shërbimi u përditësua me sukses!');
      } else {
        await adminCreateService(formData);
        toast.success('Shërbimi u krijua me sukses!');
      }
      setIsDialogOpen(false);
      fetchServices();
    } catch (err) {
      console.error('Error saving service:', err);
      toast.error('Gabim gjatë ruajtjes së shërbimit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;

    try {
      await adminDeleteService(selectedService.id);
      toast.success('Shërbimi u fshi me sukses!');
      setIsDeleteDialogOpen(false);
      fetchServices();
    } catch (err) {
      console.error('Error deleting service:', err);
      toast.error('Gabim gjatë fshirjes së shërbimit');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-services-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Shërbimet</h1>
          <p className="text-[#71717A]">Menaxho shërbimet e kompanisë</p>
        </div>
        <Button 
          onClick={handleOpenCreate}
          className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A]"
          data-testid="add-service-button"
        >
          <Plus size={18} className="mr-2" />
          Shto Shërbim
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
        <Input
          type="text"
          placeholder="Kërko shërbime..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-services"
        />
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-sm border border-[#E4E4E7] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th className="text-left px-6 py-4">Titulli (DE)</th>
                <th className="text-left px-6 py-4">Slug</th>
                <th className="text-left px-6 py-4">Statusi</th>
                <th className="text-left px-6 py-4">Krijuar</th>
                <th className="text-right px-6 py-4">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map((service) => (
                <tr key={service.id} className="border-t border-[#E4E4E7]">
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#0A0A0A]">{service.title_de}</div>
                    <div className="text-sm text-[#71717A] truncate max-w-xs">{service.short_de}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#71717A]">{service.slug}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                      service.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {service.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {service.is_active ? 'Aktiv' : 'Joaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#71717A]">{formatDate(service.created_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(service)}
                        className="text-[#71717A] hover:text-[#D4B982]"
                        data-testid={`edit-service-${service.id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDelete(service)}
                        className="text-[#71717A] hover:text-red-500"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredServices.length === 0 && (
          <div className="p-12 text-center">
            <p className="text-[#71717A]">
              {searchTerm ? 'Nuk u gjet asnjë shërbim' : 'Nuk ka shërbime ende'}
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedService ? 'Ndrysho Shërbimin' : 'Shto Shërbim të Ri'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* German Section */}
            <div className="border-b border-[#E4E4E7] pb-4 mb-4">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                🇩🇪 Gjermanisht (Kryesore)
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title_de">Titulli (DE) *</Label>
                  <Input
                    id="title_de"
                    value={formData.title_de}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="service-title"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="service-slug"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="short_de">Përshkrim i shkurtër (DE) *</Label>
                <Textarea
                  id="short_de"
                  value={formData.short_de}
                  onChange={(e) => setFormData({ ...formData, short_de: e.target.value })}
                  required
                  rows={2}
                  className="mt-1"
                  data-testid="service-short"
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="long_de">Përshkrim i plotë (DE) *</Label>
                <Textarea
                  id="long_de"
                  value={formData.long_de}
                  onChange={(e) => setFormData({ ...formData, long_de: e.target.value })}
                  required
                  rows={4}
                  className="mt-1"
                  data-testid="service-long"
                />
              </div>
            </div>

            {/* English Section */}
            <div className="border-b border-[#E4E4E7] pb-4 mb-4">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                🇬🇧 Anglisht (Opsionale)
              </h3>
              
              <div>
                <Label htmlFor="title_en">Titulli (EN)</Label>
                <Input
                  id="title_en"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="mt-1"
                  placeholder="English title"
                  data-testid="service-title-en"
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="short_en">Përshkrim i shkurtër (EN)</Label>
                <Textarea
                  id="short_en"
                  value={formData.short_en}
                  onChange={(e) => setFormData({ ...formData, short_en: e.target.value })}
                  rows={2}
                  className="mt-1"
                  placeholder="Short description in English"
                  data-testid="service-short-en"
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="long_en">Përshkrim i plotë (EN)</Label>
                <Textarea
                  id="long_en"
                  value={formData.long_en}
                  onChange={(e) => setFormData({ ...formData, long_en: e.target.value })}
                  rows={4}
                  className="mt-1"
                  placeholder="Full description in English"
                  data-testid="service-long-en"
                />
              </div>
            </div>

            {/* Common Fields */}
            <div>
              <Label htmlFor="icon">Ikona</Label>
              <Input
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="mt-1"
                placeholder="bi-tools"
              />
            </div>

            {/* Image Upload Section */}
            <div className="border border-[#E4E4E7] rounded-lg p-4 bg-[#FAFAFA]">
              <h4 className="font-medium text-[#0A0A0A] mb-4">📷 Imazhet</h4>
              
              <div className="space-y-6">
                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  label="Imazhi Kryesor"
                  multiple={false}
                />

                <ImageUploader
                  value={formData.gallery}
                  onChange={(urls) => setFormData({ ...formData, gallery: urls })}
                  label="Galeria (mund të shtoni shumë imazhe)"
                  multiple={true}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="features">Veçoritë (DE) - një për rresht</Label>
                <Textarea
                  id="features"
                  value={formData.features?.join('\n') || ''}
                  onChange={(e) => handleArrayChange('features', e.target.value)}
                  rows={3}
                  className="mt-1"
                  placeholder="Veçoria 1&#10;Veçoria 2"
                />
              </div>
              <div>
                <Label htmlFor="features_en">Veçoritë (EN) - një për rresht</Label>
                <Textarea
                  id="features_en"
                  value={formData.features_en?.join('\n') || ''}
                  onChange={(e) => handleArrayChange('features_en', e.target.value)}
                  rows={3}
                  className="mt-1"
                  placeholder="Feature 1&#10;Feature 2"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="target_audience">Audienca e synuar (DE)</Label>
                <Textarea
                  id="target_audience"
                  value={formData.target_audience || ''}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="target_audience_en">Audienca e synuar (EN)</Label>
                <Textarea
                  id="target_audience_en"
                  value={formData.target_audience_en || ''}
                  onChange={(e) => setFormData({ ...formData, target_audience_en: e.target.value })}
                  rows={2}
                  className="mt-1"
                  placeholder="Target audience in English"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="process">Procesi (DE)</Label>
                <Textarea
                  id="process"
                  value={formData.process || ''}
                  onChange={(e) => setFormData({ ...formData, process: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="process_en">Procesi (EN)</Label>
                <Textarea
                  id="process_en"
                  value={formData.process_en || ''}
                  onChange={(e) => setFormData({ ...formData, process_en: e.target.value })}
                  rows={2}
                  className="mt-1"
                  placeholder="Process in English"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="benefits">Përfitimet (DE) - një për rresht</Label>
                <Textarea
                  id="benefits"
                  value={formData.benefits?.join('\n') || ''}
                  onChange={(e) => handleArrayChange('benefits', e.target.value)}
                  rows={3}
                  className="mt-1"
                  placeholder="Përfitimi 1&#10;Përfitimi 2"
                />
              </div>
              <div>
                <Label htmlFor="benefits_en">Përfitimet (EN) - një për rresht</Label>
                <Textarea
                  id="benefits_en"
                  value={formData.benefits_en?.join('\n') || ''}
                  onChange={(e) => handleArrayChange('benefits_en', e.target.value)}
                  rows={3}
                  className="mt-1"
                  placeholder="Benefit 1&#10;Benefit 2"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Shërbimi aktiv</Label>
            </div>

            {/* SEO Section */}
            <div className="border-t border-[#E4E4E7] pt-6 mt-6">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4">SEO & Meta Tags</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title || ''}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    className="mt-1"
                    placeholder="Titulli për motorët e kërkimit"
                  />
                  <p className="text-xs text-[#71717A] mt-1">
                    Rekomandohet: 50-60 karaktere
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description || ''}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    rows={2}
                    className="mt-1"
                    placeholder="Përshkrim i shkurtër për motorët e kërkimit"
                  />
                  <p className="text-xs text-[#71717A] mt-1">
                    Rekomandohet: 150-160 karaktere
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_keywords">Meta Keywords</Label>
                  <Input
                    id="meta_keywords"
                    value={formData.meta_keywords || ''}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    className="mt-1"
                    placeholder="fjalë, kyçe, për, këtë, shërbim"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Anulo
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A]"
                data-testid="service-submit"
              >
                {isSubmitting ? 'Duke ruajtur...' : (selectedService ? 'Ruaj Ndryshimet' : 'Krijo Shërbimin')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmo Fshirjen</AlertDialogTitle>
            <AlertDialogDescription>
              Jeni të sigurt që dëshironi të fshini shërbimin "{selectedService?.title_de}"? 
              Ky veprim nuk mund të kthehet prapa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulo</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete"
            >
              Fshi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
